import { IDatasetTypeMetadata } from 'app/extensibility/metadatas/resource-type/dataset-type-metadata';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { ColumnPhysicalType, ColumnType } from './column-type.util';
import { Column } from 'app/model/dataset/column';
import shellapi from 'a365-shell-api';
import { Model } from 'app/model/backendmodel';
import { DelimitedTextDataset } from 'shell-models-lib';
import { ExcelDataset } from 'app/model/dataset/excel.dataset';
import { CopyActivity } from 'app/model/pipeline/copy.activity';
import { IPropertyBag } from 'app/extensibility/property-bag';
import { supportEditPhysicalType } from './dataset-utils';

export function supportSqlEditSinkSchemaPhysicalType(datasetConfig: IDatasetTypeMetadata, entity?: IPropertyBag): boolean {
    // note: for simplicity, setting `entity` as optional since all SQL family datasets uses boolean supportEditPhysicalType for now
    const sqlTypes = [
        adfModelTypeMap.azureSql.datasetType,
        adfModelTypeMap.azureSqlMI.datasetType,
        adfModelTypeMap.sqlServer.datasetType,
        adfModelTypeMap.azureSqlDW.datasetType,
        __IS_FABRIC_DPE__ ? adfModelTypeMap.DataWarehouse.datasetType : adfModelTypeMap.Warehouse.datasetType,
        adfModelTypeMap.fabricSql.datasetType,
        adfModelTypeMap.sqlAnalyticsEndpoint.datasetType
    ];
    return __IS_FABRIC_DPE__ && supportEditPhysicalType(datasetConfig, entity) && sqlTypes.includes(datasetConfig.type);
}

export function generatePrecisionFromSource(
    sourcePrecision: number,
    physicalType: ColumnPhysicalType,
    sinkMetadata: IDatasetTypeMetadata,
    logicalType?: ColumnType
): number {
    let sinkPrecision;
    const precisionConfig = sinkMetadata.precisionMap?.[physicalType];
    let useFallback = false;
    if (precisionConfig) {
        const minValue = precisionConfig.minValue;
        const maxValue = precisionConfig.maxValue;
        const defaultValue = precisionConfig.defaultValue;
        sinkPrecision = sourcePrecision ?? defaultValue;
        if (sinkPrecision == null) {
            if (precisionConfig.allowNull) {
                sinkPrecision = undefined;
            } else {
                sinkPrecision = maxValue;
                useFallback = true;
            }
        } else if (sinkPrecision > maxValue || sinkPrecision < minValue) {
            sinkPrecision = maxValue;
        }
    }
    if (logicalType && sinkMetadata.logicalToPhysicalDefaultRange?.[logicalType] && (sinkPrecision == null || useFallback)) {
        sinkPrecision = sinkMetadata.logicalToPhysicalDefaultRange?.[logicalType]?.precision;
    }
    return sinkPrecision;
}

export function generateScaleFromSource(
    sourceScale: number,
    physicalType: ColumnPhysicalType,
    sinkMetadata: IDatasetTypeMetadata,
    logicalType?: ColumnType
): number {
    let sinkScale;
    const scaleConfig = sinkMetadata.scaleMap?.[physicalType];
    let useFallback = false;
    if (scaleConfig) {
        const minValue = scaleConfig.minValue;
        const maxValue = scaleConfig.maxValue;
        const defaultValue = scaleConfig.defaultValue;
        sinkScale = sourceScale ?? defaultValue;
        if (sinkScale == null) {
            if (scaleConfig.allowNull) {
                sinkScale = undefined;
            } else {
                sinkScale = maxValue;
                useFallback = true;
            }
        } else if (sinkScale > maxValue || sinkScale < minValue) {
            sinkScale = maxValue;
        }
    }
    if (logicalType && sinkMetadata.logicalToPhysicalDefaultRange?.[logicalType] && (sinkScale == null || useFallback)) {
        sinkScale = sinkMetadata.logicalToPhysicalDefaultRange?.[logicalType]?.scale;
    }
    return sinkScale;
}

export function generateLengthFromSource(
    sourceLength: string,
    physicalType: ColumnPhysicalType,
    sinkMetadata: IDatasetTypeMetadata,
    disableMaxValue: boolean = false,
    logicalType?: ColumnType
): string {
    let sinkLength;
    const lengthConfig = sinkMetadata.binaryLengthMap?.[physicalType];
    let useFallback = false;
    if (lengthConfig) {
        const minLengthValue = lengthConfig.minValue;
        const maxLengthValue =
            lengthConfig.supportMaxValue &&
                !disableMaxValue &&
                (!lengthConfig.defaultValue || lengthConfig.defaultValue === 'MAX')
                ? 'MAX'
                : lengthConfig.maxValue?.toString();
        const defaultValue = lengthConfig.defaultValue?.toString();
        sinkLength = sourceLength ?? defaultValue;
        if (sinkLength == null) {
            useFallback = true;
            sinkLength = maxLengthValue;
        } else if (
            (sinkLength.toLowerCase() === 'max' && !lengthConfig.supportMaxValue) ||
            (sinkLength.toLowerCase() !== 'max' &&
                (parseInt(sinkLength, 10) > parseInt(maxLengthValue, 10) || parseInt(sinkLength, 10) < minLengthValue))
        ) {
            sinkLength = maxLengthValue?.toString();
        }
    }
    if (logicalType && sinkMetadata.logicalToPhysicalDefaultRange?.[logicalType] && (sinkLength == null || useFallback)) {
        sinkLength = sinkMetadata.logicalToPhysicalDefaultRange?.[logicalType]?.length?.toString();
    }
    return sinkLength;
}

export function generateSinkColumnPropertiesFromSource(
    sourceColumn: Column,
    sinkColumn: Column,
    sinkMetadata: IDatasetTypeMetadata,
    preserveSink?: boolean
): void {
    const isSqlSink = supportSqlEditSinkSchemaPhysicalType(sinkMetadata);
    const sourceSchemaLogicalType = sourceColumn.logicalType;
    const sinkSchemaLogicalType = sinkColumn.logicalType;
    const sourceSchemaPhysicalType: ColumnPhysicalType = sourceColumn.physicalType?.toLowerCase() as ColumnPhysicalType;
    const sinkSchemaPhysicalType: ColumnPhysicalType = sinkColumn.physicalType?.toLowerCase() as ColumnPhysicalType;

    const sourcePrecision =
        sourceSchemaPhysicalType === sinkSchemaPhysicalType || sourceSchemaLogicalType === sinkSchemaLogicalType
            ? sourceColumn.precision
            : undefined;
    const sourceScale =
        sourceSchemaPhysicalType === sinkSchemaPhysicalType || sourceSchemaLogicalType === sinkSchemaLogicalType
            ? sourceColumn.scale
            : undefined;
    const isOracleBinaryType = sinkMetadata.type === adfModelTypeMap.oracle.copySinkType &&
        sinkMetadata.binaryPhysicalTypes?.includes(sinkSchemaPhysicalType) &&
        sinkMetadata.binaryPhysicalTypes?.includes(sourceSchemaPhysicalType); // size is interchangeable between Oracle binary types
    const sourceSize = sourceSchemaPhysicalType === sinkSchemaPhysicalType || isOracleBinaryType ? sourceColumn.size : undefined;

    if (isSqlSink) {
        if (sinkSchemaPhysicalType === ColumnPhysicalType.Numeric || sinkSchemaPhysicalType === ColumnPhysicalType.Decimal) {
            sinkColumn.precision = generatePrecisionFromSource(
                sourcePrecision ?? (preserveSink ? sinkColumn.precision : undefined),
                sinkSchemaPhysicalType as ColumnPhysicalType,
                sinkMetadata
            );
            sinkColumn.scale = generateScaleFromSource(
                sourceScale ?? (preserveSink ? sinkColumn.scale : undefined),
                sinkSchemaPhysicalType as ColumnPhysicalType,
                sinkMetadata
            );
        } else if (sinkSchemaPhysicalType === ColumnPhysicalType.Time) {
            const sourceScale = sourceSchemaPhysicalType === sinkSchemaPhysicalType ? sourceColumn.scale : undefined;
            sinkColumn.scale = generateScaleFromSource(
                sourceScale ?? (preserveSink ? sinkColumn.scale : undefined),
                sinkSchemaPhysicalType as ColumnPhysicalType,
                sinkMetadata
            );
        } else if (
            sinkSchemaPhysicalType === ColumnPhysicalType.Datetime2 ||
            sinkSchemaPhysicalType === ColumnPhysicalType.Datetimeoffset
        ) {
            const sourcePrecision =
                sourceSchemaPhysicalType === sinkSchemaPhysicalType ? sourceColumn.precision : undefined;
            sinkColumn.precision = generatePrecisionFromSource(
                sourcePrecision ?? (preserveSink ? sinkColumn.precision : undefined),
                sinkSchemaPhysicalType as ColumnPhysicalType,
                sinkMetadata
            );
        } else if (sinkMetadata.binaryPhysicalTypes?.indexOf(sinkSchemaPhysicalType) > -1) {
            sinkColumn.size = generateLengthFromSource(
                sourceSize ?? (preserveSink ? sinkColumn.size : undefined),
                sinkSchemaPhysicalType as ColumnPhysicalType,
                sinkMetadata
            );
        }
    } else {
        const logicalType = sinkMetadata.logicalToPhysicalDefaultRange ? (sourceColumn.logicalType || sourceColumn.columnType) as ColumnType : undefined;
        sinkColumn.precision = generatePrecisionFromSource(
            sourcePrecision ?? (preserveSink ? sinkColumn.precision : undefined),
            sinkSchemaPhysicalType as ColumnPhysicalType,
            sinkMetadata,
            logicalType
        );
        sinkColumn.scale = generateScaleFromSource(
            sourceScale ?? (preserveSink ? sinkColumn.scale : undefined),
            sinkSchemaPhysicalType as ColumnPhysicalType,
            sinkMetadata,
            logicalType
        );
        sinkColumn.size = generateLengthFromSource(
            sourceSize ?? (preserveSink ? sinkColumn.size : undefined),
            sinkSchemaPhysicalType as ColumnPhysicalType,
            sinkMetadata,
            false,
            logicalType
        );
        sinkColumn.unitOptions = sinkMetadata.binaryLengthMap?.[sinkSchemaPhysicalType]?.unitList?.map((unit) => {
            return {
                label: unit,
                value: unit
            };
        });
        sinkColumn.unit = sourceColumn.unit ?? sinkMetadata.binaryLengthMap?.[sinkSchemaPhysicalType]?.defaultUnit;
        sinkColumn.required = sinkMetadata.binaryLengthMap?.[sinkSchemaPhysicalType]?.required;
    }
}

export function generateSqlSinkSchemaFromSource(
    sourceSchema: shellapi.queryAPI.IColumn,
    sinkSchema: shellapi.queryAPI.IColumn,
    sinkMetadata: IDatasetTypeMetadata
): void {
    const isSqlSink = supportSqlEditSinkSchemaPhysicalType(sinkMetadata);
    const sourceSchemaLogicalType = sourceSchema.logicalDataType;
    const sinkSchemaLogicalType = sinkSchema.logicalDataType;
    const sourceSchemaPhysicalType = sourceSchema.nativeDataType?.toLowerCase();
    const sinkSchemaPhysicalType = sinkSchema.nativeDataType?.toLowerCase();
    if (
        sinkSchemaPhysicalType === ColumnPhysicalType.Decimal ||
        (isSqlSink && sinkSchemaPhysicalType === ColumnPhysicalType.Numeric)
    ) {
        const sourcePrecision =
            sourceSchemaPhysicalType === sinkSchemaPhysicalType || sourceSchemaLogicalType === sinkSchemaLogicalType
                ? sourceSchema.precision
                : undefined;
        sinkSchema.precision = generatePrecisionFromSource(
            sourcePrecision,
            sinkSchemaPhysicalType as ColumnPhysicalType,
            sinkMetadata
        );
        const sourceScale =
            sourceSchemaPhysicalType === sinkSchemaPhysicalType || sourceSchemaLogicalType === sinkSchemaLogicalType
                ? sourceSchema.scale
                : undefined;
        sinkSchema.scale = generateScaleFromSource(
            sourceScale,
            sinkSchemaPhysicalType as ColumnPhysicalType,
            sinkMetadata
        );
    } else if (isSqlSink) {
        if (sinkSchemaPhysicalType === ColumnPhysicalType.Time) {
            const sourceScale = sourceSchemaPhysicalType === sinkSchemaPhysicalType ? sourceSchema.scale : undefined;
            sinkSchema.scale = generateScaleFromSource(
                sourceScale,
                sinkSchemaPhysicalType as ColumnPhysicalType,
                sinkMetadata
            );
        }
        if (
            sinkSchemaPhysicalType === ColumnPhysicalType.Datetime2 ||
            sinkSchemaPhysicalType === ColumnPhysicalType.Datetimeoffset
        ) {
            // For datatime2 and datetimeoffset, we use the sale value returned from the api as the column precision value
            const sourceScale = sourceSchemaPhysicalType === sinkSchemaPhysicalType ? sourceSchema.scale : undefined;
            sinkSchema.scale = generatePrecisionFromSource(
                sourceScale,
                sinkSchemaPhysicalType as ColumnPhysicalType,
                sinkMetadata
            );
        }

        if (sinkMetadata.binaryPhysicalTypes?.indexOf(sinkSchemaPhysicalType) > -1) {
            const sourceSize =
                sourceSchemaPhysicalType === sinkSchemaPhysicalType ? sourceSchema.size?.toString() : undefined;
            const size = generateLengthFromSource(
                sourceSize,
                sinkSchemaPhysicalType as ColumnPhysicalType,
                sinkMetadata,
                true
            );

            if (size) {
                sinkSchema.size = parseInt(size, 10);
            }
        }
    }
}

export function generateSinkSchemaFromSource(
    sourceSchema: shellapi.queryAPI.IColumn,
    sinkSchema: shellapi.queryAPI.IColumn,
    sinkMetadata: IDatasetTypeMetadata,
    preserveSink?: boolean
): void {
    const isSqlSink = supportSqlEditSinkSchemaPhysicalType(sinkMetadata);
    if (isSqlSink) {
        generateSqlSinkSchemaFromSource(sourceSchema, sinkSchema, sinkMetadata);
        return;
    }
    const sourceSchemaLogicalType = sourceSchema.logicalDataType;
    const sinkSchemaLogicalType = sinkSchema.logicalDataType;
    const sourceSchemaPhysicalType = sourceSchema.nativeDataType?.toLowerCase();
    const sinkSchemaPhysicalType = sinkSchema.nativeDataType?.toLowerCase();
    const sourcePrecision =
        sourceSchemaPhysicalType === sinkSchemaPhysicalType || sourceSchemaLogicalType === sinkSchemaLogicalType
            ? sourceSchema.precision
            : undefined;
    const sourceScale =
        sourceSchemaPhysicalType === sinkSchemaPhysicalType || sourceSchemaLogicalType === sinkSchemaLogicalType
            ? sourceSchema.scale
            : undefined;
    const isOracleBinaryType = sinkMetadata.type === adfModelTypeMap.oracle.copySinkType &&
        sinkMetadata.binaryPhysicalTypes?.includes(sinkSchemaPhysicalType) &&
        sinkMetadata.binaryPhysicalTypes?.includes(sourceSchemaPhysicalType); // size is interchangeable between Oracle binary types
    const sourceSize = sourceSchemaPhysicalType === sinkSchemaPhysicalType || isOracleBinaryType ? sourceSchema.size?.toString() : undefined;
    const logicalType = sinkMetadata.logicalToPhysicalDefaultRange ? sourceSchemaLogicalType as ColumnType : undefined;

    sinkSchema.precision = generatePrecisionFromSource(
        sourcePrecision ?? (preserveSink ? sinkSchema.precision : undefined),
        sinkSchemaPhysicalType as ColumnPhysicalType,
        sinkMetadata,
        logicalType
    );

    sinkSchema.scale = generateScaleFromSource(
        sourceScale ?? (preserveSink ? sinkSchema.scale : undefined),
        sinkSchemaPhysicalType as ColumnPhysicalType,
        sinkMetadata,
        logicalType
    );

    const size = generateLengthFromSource(
        sourceSize ?? (preserveSink ? sinkSchema.size?.toString() : undefined),
        sinkSchemaPhysicalType as ColumnPhysicalType,
        sinkMetadata,
        false,
        logicalType
    );
    if (size) {
        sinkSchema.size = parseInt(size, 10);
        sinkSchema.unit = sinkSchema.unit;
    }
}

export function toMappingItem(
    activity: CopyActivity,
    keepSinkPhysicalType: boolean,
    typeConversionSupported: boolean,
    sourceShowLogicType: boolean,
    isSourceSql: boolean,
    isSinkSql: boolean,
    col: Column,
    isSource?: boolean,
    index?: number,
    useParquetSinkType?: boolean
): Model.MappingItem {
    if (col == null) {
        return {};
    }
    // if sink is by index, we treat the mapping order as sink index
    const sinkByIndex =
        !isSource &&
        (activity.sinkDataset instanceof DelimitedTextDataset || activity.sinkDataset instanceof ExcelDataset) &&
        !activity.sinkDataset.firstRowAsHeader;
    const isSqlSource = isSource && isSourceSql;
    const isSqlSink = !isSource && isSinkSql;
    return {
        name: sinkByIndex ? undefined : col.name,
        type:
            col.columnType === ColumnType.UnsupportedType ||
                (keepSinkPhysicalType && !isSource && !activity.hasExistingMapping)
                ? undefined
                : col.columnType,
        physicalType:
            ((typeConversionSupported && activity.enableTypeConversion) ||
                (keepSinkPhysicalType && (!isSource || !sourceShowLogicType))) &&
                !(useParquetSinkType && col.parquetPorperties)
                ? col.physicalType
                : undefined,
        parquetProperties:
            useParquetSinkType && col.parquetPorperties
                ? {
                    logicalType: col.parquetPorperties.logicalType == null ? undefined : col.parquetPorperties.logicalType,
                    physicalType: col.parquetPorperties.physicalType,
                    unit: col.parquetPorperties.unit
                }
                : undefined,
        isNullable: col.isNullable,
        format: col.format,
        ordinal: sinkByIndex ? index + 1 : col.index,
        culture: col.culture,
        path: sinkByIndex ? undefined : col.path,
        scale:
            (typeConversionSupported && activity.enableTypeConversion) ||
                (!isSource && keepSinkPhysicalType) ||
                isSqlSource ||
                useParquetSinkType
                ? col.scale
                : undefined,
        precision:
            (typeConversionSupported && activity.enableTypeConversion) ||
                (!isSource && keepSinkPhysicalType) ||
                isSqlSource ||
                useParquetSinkType
                ? col.precision
                : undefined,
        length:
            col.size && (
                isSqlSource ||
                isSqlSink ||
                activity.hasExistingMapping ||
                keepSinkPhysicalType
            )
                ? col.size
                : undefined,
        unit: col.unit && (activity.hasExistingMapping || keepSinkPhysicalType) ? col.unit : undefined
    };
}

export function getLogicalTypeByPhysicalType(datasetConfig: IDatasetTypeMetadata, physicalType: string, connectorType?: string): string {
    const logicalType = datasetConfig?.PhysicalToLogicalMapping?.[physicalType];
    if (typeof logicalType === 'string') {
        return logicalType;
    } else if (typeof logicalType === 'object') {
        return connectorType ? logicalType[connectorType] : logicalType['default'];
    }

    return undefined;
}